import { sha512_256 } from "js-sha512";

import { ShopPurchaseData } from "../shop";

import { GameStorage } from "./storage";
import { ProgressChecker } from "./progress-checker";

export const Cloud = {
  user: null,
  lastCloudHash: null,
  isAvailable: typeof YaGames !== "undefined",
  resetTempState() {
    this.lastCloudHash = null;
    GameStorage.lastCloudSave = Date.now();
    GameIntervals.checkCloudSave.restart();
  },

  get loggedIn() {
    return this.user !== null;
  },

  login() {
    if (!this.isAvailable || this.loggedIn) {
      return;
    }

    ysdk.auth.openAuthDialog().then(() => {
      ysdk.getPlayer().then(async _player => {
        window.playerYG = _player;
        await this.init();

        if (player.options.hideGoogleName) GameUI.notify.success(`Успешная авторизация аккаунта`);
        else GameUI.notify.success(`Успешная авторизация как ${this.user.displayName}`);
        if (ShopPurchaseData.isIAPEnabled) Speedrun.setSTDUse(true);
      });
    }).catch(() => {
      GameUI.notify.error("Вход в аккаунт провален");
    });
  },

  compareSaves(cloud, local, hash) {
    // This try/except will generally only throw an exception if the cloud save is somehow malformed.
    // In practice this should only happen for saves which are really old, or from very early development.
    // This will be handled upstream by showing a modal notifying the player of the invalid data and giving them
    // options to resolve it without needing to open up the console.
    // Note: This could also technically happen if the local save is malformed instead - this shouldn't
    // happen unless the player is overtly cheating through the console, and in that case it seems unreasonable
    // to attempt to handle such open-ended behavior gracefully
    try {
      return {
        farther: ProgressChecker.compareSaveProgress(cloud, local),
        older: ProgressChecker.compareSaveTimes(cloud, local),
        differentName: cloud?.options.saveFileName !== local?.options.saveFileName,
        hashMismatch: this.lastCloudHash && this.lastCloudHash !== hash,
      };
    } catch (err) {
      return null;
    }
  },

  async saveCheck(forceModal = false) {
    if (!this.isAvailable) {
      return;
    }

    const saveId = GameStorage.currentSlot;
    const cloudSave = await this.load();
    if (cloudSave === null) {
      this.save();
    } else {
      const thisCloudHash = sha512_256(GameSaveSerializer.serialize(cloudSave));
      if (!this.lastCloudHash) this.lastCloudHash = thisCloudHash;
      const localSave = GameStorage.saves[saveId];
      const saveComparison = this.compareSaves(cloudSave, localSave, thisCloudHash);
      const overwriteAndSendCloudSave = () => this.save();

      // If the comparison fails, we assume the cloud data is corrupted and show the relevant modal
      if (!saveComparison) {
        Modal.addCloudConflict(saveId, saveComparison, cloudSave, localSave, overwriteAndSendCloudSave);
        Modal.cloudInvalidData.show({ isSaving: true });
        return;
      }

      // Bring up the modal if cloud saving will overwrite a cloud save which is older or possibly farther
      const hasBoth = cloudSave && localSave;
      // NOTE THIS CHECK IS INTENTIONALLY DIFFERENT FROM THE LOAD CHECK
      const hasConflict = hasBoth && saveComparison && (saveComparison.older === -1 || saveComparison.farther === -1 ||
        saveComparison.differentName || saveComparison.hashMismatch);
      if (forceModal || (hasConflict && player.options.showCloudModal)) {
        Modal.addCloudConflict(saveId, saveComparison, cloudSave, localSave, overwriteAndSendCloudSave);
        Modal.cloudSaveConflict.show();
      } else if (!hasConflict || player.options.forceCloudOverwrite) {
        overwriteAndSendCloudSave();
      }
    }
  },

  save() {
    if (!this.user) return;
    if (GlyphSelection.active || ui.$viewModel.modal.progressBar !== undefined) return;
    if (player.options.syncSaveIntervals) GameStorage.save();
    const serializedSave = GameSaveSerializer.serialize(GameStorage.saves[GameStorage.currentSlot]);

    this.lastCloudHash = sha512_256(serializedSave);
    GameStorage.lastCloudSave = Date.now();
    GameIntervals.checkCloudSave.restart();

    const slot = GameStorage.currentSlot;

    playerYG.getData().then(_data => {
      _data[slot] = serializedSave;
      playerYG.setData(_data).then(() => {
        if (player.options.hideGoogleName) GameUI.notify.info(`Игра сохранена (слот ${slot + 1}) в облако`);
        else GameUI.notify.info(`Игра сохранена (слот ${slot + 1}) в облако пользователя ${this.user.displayName}`);
      }).catch(() => {
        GameUI.notify.info(`Не удалось сохранить в Облако (слот ${slot + 1})`);
      });
    });
  },

  // This function actually fetches a save with load() and loads it into the game
  async loadCheck() {
    if (!this.isAvailable) {
      return;
    }

    const save = await this.load();
    if (save === null) {
      if (player.options.hideGoogleName) GameUI.notify.info(`Нет облачного сохранения для текущего Аккаунта`);
      else GameUI.notify.info(`Нет облачного сохранения для пользователя ${this.user.displayName}`);
    } else {
      const cloudSave = save;
      const saveId = GameStorage.currentSlot;
      const localSave = GameStorage.saves[saveId];
      const saveComparison = this.compareSaves(cloudSave, localSave);

      // eslint-disable-next-line no-loop-func
      const overwriteLocalSave = () => {
        GameStorage.overwriteSlot(saveId, cloudSave);

        if (player.options.hideGoogleName) GameUI.notify.info(`Облачное сохранение (слот ${saveId + 1}) загружено`);
        else GameUI.notify.info(`Облачное сохранение (слот ${saveId + 1}) загружено для пользователя
          ${this.user.displayName}`);
      };

      // If the comparison fails, we assume the cloud data is corrupted and show the relevant modal
      if (!saveComparison) {
        Modal.addCloudConflict(saveId, saveComparison, cloudSave, localSave, overwriteLocalSave);
        Modal.cloudInvalidData.show({ isSaving: false });
        return;
      }

      // Bring up the modal if cloud loading will overwrite a local save which is older or possibly farther
      const hasBoth = cloudSave && localSave;
      const hasConflict = hasBoth && (saveComparison.older === 1 || saveComparison.farther !== -1 ||
        saveComparison.differentName);
      if (hasConflict) {
        Modal.addCloudConflict(saveId, saveComparison, cloudSave, localSave, overwriteLocalSave);
        Modal.cloudLoadConflict.show();
      } else {
        overwriteLocalSave();
      }
    }
  },

  // Cloud.load() does not actually load a game, it just fetches a save from web
  async load() {
    let saveData = "";
    await playerYG.getData([GameStorage.currentSlot.toString()]).then(data => {
      saveData = data[GameStorage.currentSlot];
    });
    if (typeof saveData !== "undefined") return GameSaveSerializer.deserialize(saveData);
    return null;
  },

  async init() {
    if (!this.isAvailable) {
      GameStorage.load();
      return;
    }

    if (playerYG.getMode() === "lite") {
      this.user = null;
      GameStorage.load();
    } else {
      this.user = {
        displayName: playerYG.getName(),
      };
      ShopPurchaseData.setLocalSTD(await ShopPurchaseData.getRemoteSTD());
      await this.loadCheck();
    }
  },
};
